import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Select,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import currencyFormatter from "currency-formatter";
import Translate from "../../../components/LanguageTranslation/Translate";
import { useTranslation } from "../../../components/LanguageTranslation/TranslationContext";
import axiosInstance from "../../../helpers/axiosInstance";

import { useCurrency } from "../../CurrencyExchange/CurrencyContext";

const AddFlight = ({ isOpen, onClose, addTripItem, editItem = null, onEditSuccess = null }) => {
  const { useTranslate } = useTranslation();
  const placeholderText = useTranslate("Hours");
  const placeholderminute = useTranslate("Minutes");
  const { currency, convertCurrencyPref } = useCurrency();
  const [price, setPrice] = useState(0);
  const [link, setLink] = useState("");
  const [airline, setAirline] = useState("");
  const [origin, setOrigin] = useState("");
  const [departureAt, setDepartureAt] = useState("");
  const [durationHours, setDurationHours] = useState();
  const [durationMinutes, setDurationMinutes] = useState();
  const [transfers, setTransfers] = useState(0);
  const [destination, setDestination] = useState("");
  const [returnAt, setReturnAt] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [flightCurrency, setFlightCurrency] = useState(currency);
  const [rates, setRates] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const availableCurrencies = currencyFormatter.currencies.map((currency) => ({
    code: currency.code,
    label: `${currency.code} - ${currency.symbol || ""}`,
  }));

  const normalizedURL = (url) => {
    return url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;
  };

  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Reset form whenever modal opens
  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        // Populate form with existing data for editing
        setAirline(editItem.airline || "");
        setFlightNumber(editItem.flight_number || "");
        setOrigin(editItem.origin || "");
        setDestination(editItem.destination || "");
        setDepartureAt(editItem.departure_at ? new Date(editItem.departure_at).toISOString().slice(0, 16) : "");
        setReturnAt(editItem.return_at ? new Date(editItem.return_at).toISOString().slice(0, 16) : "");
        setTransfers(editItem.transfers || 0);
        setDurationHours(Math.floor((editItem.duration_to || 0) / 60));
        setDurationMinutes((editItem.duration_to || 0) % 60);
        setPrice(editItem.price || 0);
        setFlightCurrency(editItem.currency || currency);
        setLink(editItem.link || "");
      } else {
        // Reset form for new flight
        setPrice(0);
        setLink("");
        setAirline("");
        setOrigin("");
        setDepartureAt("");
        setDurationHours();
        setDurationMinutes();
        setTransfers(0);
        setDestination("");
        setReturnAt("");
        setFlightNumber("");
        setFlightCurrency(currency);
      }
      setErrors({});
    }
  }, [isOpen, editItem, currency]);

  const validateForm = () => {
    const newErrors = {};

    if (!airline.trim()) newErrors.airline = "Airline is required";
    if (!origin.trim()) newErrors.origin = "Origin is required";
    if (!destination.trim()) newErrors.destination = "Destination is required";
    if (!departureAt) newErrors.departureAt = "Departure time is required";
    if (isNaN(durationHours) || durationHours < 0)
      newErrors.durationHours = "Invalid hours";
    if (isNaN(durationMinutes) || durationMinutes < 0 || durationMinutes > 59)
      newErrors.durationMinutes = "Invalid minutes";
    if (isNaN(transfers) || transfers < 0)
      newErrors.transfers = "Invalid transfer count";
    if (isNaN(price) || price <= 0) newErrors.price = "Price must be positive";
    if (!flightNumber.trim())
      newErrors.flightNumber = "Flight number is required";
    if (!link.trim() || !isValidURL(normalizedURL(link)))
      newErrors.link = "Please enter a valid URL, e.g. https://example.com";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchRates = async () => {
    try {
      const response = await axios.get(
        "https://api.exchangerate-api.com/v4/latest/USD"
      );

      if (response.data?.rates) {
        setRates({ ...response.data.rates, USD: 1 });
      } else {
        throw new Error("Invalid rates data");
      }
    } catch (error) {
      toast({
        title: "Error fetching exchange rates",
        description: "Using default conversion rates",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      setRates({ USD: 1, EUR: 0.85, GBP: 0.75 });
    }
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const convertCurrency = (amount, currency) => {
    if (!rates || !rates[currency]) {
      toast({
        title: "Conversion error",
        description: `Unable to convert ${currency}, using USD as fallback`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return amount;
    }
    return (amount / rates[currency]).toFixed(2);
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const convertedPrice = convertCurrency(price, flightCurrency);
      const durationTo =
        parseInt(durationHours) * 60 + parseInt(durationMinutes);

      const flight = {
        airline,
        flight_number: flightNumber,
        origin,
        destination,
        departure: departureAt,
        return: returnAt,
        number_of_transfers: transfers,
        duration_hours: parseInt(durationHours),
        duration_minutes: parseInt(durationMinutes),
        price: convertedPrice,
        currency: flightCurrency,
        link: normalizedURL(link),
      };

      // Add item_id to payload if editing
      if (editItem && editItem._id) {
        flight.item_id = editItem._id;
      }

      const response = await axiosInstance.post(
        "/bucket-list/add-manual-flight",
        flight
      );

      if (response.data.success) {
        const flightData = {
          airline,
          flight_number: flightNumber,
          origin,
          destination,
          departure_at: departureAt,
          return_at: returnAt,
          transfers: transfers,
          duration_to: durationTo,
          price: convertedPrice,
          currency: flightCurrency,
          link: normalizedURL(link),
          type: "flight",
        };

        if (editItem && editItem._id) {
          // Update existing item
          const updatedItem = {
            ...editItem,
            ...flightData,
          };
          if (onEditSuccess) {
            onEditSuccess(updatedItem);
          }
        } else {
          // Add new item
          const newItem = {
            _id: response.data.data._id,
            data: flightData,
            category: "transportation",
            preference: "flight",
            data_saved: "manual",
            email: localStorage.getItem("email"),
          };

          addTripItem(newItem);
        }
        
        toast({
          title: editItem ? "Flight updated" : "Flight added",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(response.data.message || "Failed to save flight");
      }
    } catch (error) {
      toast({
        title: "Error saving flight",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent h="80vh" overflowY="auto">
        <ModalHeader>
          <Translate text={editItem ? "Edit Flight" : "Add Flights"} />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Responsive grid: 1 column on base/small screens, 2 on md and above */}
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
            <FormControl isInvalid={errors.airline}>
              <FormLabel>
                {" "}
                <Translate text="Airline" />
              </FormLabel>
              <Input
                value={airline}
                onChange={(e) => setAirline(e.target.value)}
              />
              <FormErrorMessage>
                <Translate text={errors.airline} />
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.flightNumber}>
              <FormLabel>
                {" "}
                <Translate text="Flight Number" />
              </FormLabel>
              <Input
                value={flightNumber}
                onChange={(e) => setFlightNumber(e.target.value)}
              />
              <FormErrorMessage>
                <Translate text={errors.flightNumber} />
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.origin}>
              <FormLabel>
                <Translate text="Origin" />
              </FormLabel>
              <Input
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
              />
              <FormErrorMessage>
                <Translate text={errors.origin} />
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.destination}>
              <FormLabel>
                <Translate text="Destination" />
              </FormLabel>
              <Input
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              />
              <FormErrorMessage>
                <Translate text={errors.destination} />
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.departureAt}>
              <FormLabel>
                <Translate text="Departure" />
              </FormLabel>
              <Input
                type="datetime-local"
                value={departureAt}
                onChange={(e) => setDepartureAt(e.target.value)}
              />
              <FormErrorMessage>
                <Translate text={errors.departureAt} />
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>
                {" "}
                <Translate text="Return" />
              </FormLabel>
              <Input
                type="datetime-local"
                value={returnAt}
                onChange={(e) => setReturnAt(e.target.value)}
              />
            </FormControl>

            <FormControl isInvalid={errors.transfers}>
              <FormLabel>
                {" "}
                <Translate text="Number of Transfers" />
              </FormLabel>
              <Input
                type="number"
                min="0"
                value={transfers}
                onChange={(e) => setTransfers(e.target.value)}
              />
              <FormErrorMessage>
                {" "}
                <Translate text={errors.transfers} />
              </FormErrorMessage>
            </FormControl>

            <FormControl
              isInvalid={errors.durationHours || errors.durationMinutes}
            >
              <FormLabel>
                {" "}
                <Translate text="Flight Duration" />
              </FormLabel>
              <HStack>
                <Input
                  type="number"
                  min="0"
                  placeholder={placeholderText}
                  value={durationHours}
                  onChange={(e) => setDurationHours(e.target.value)}
                />
                <Input
                  type="number"
                  min="0"
                  max="59"
                  placeholder={placeholderminute}
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                />
              </HStack>
              <FormErrorMessage>
                <Translate
                  text={errors.durationHours || errors.durationMinutes}
                />
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.price}>
              <FormLabel>
                {" "}
                <Translate text="Price" />
              </FormLabel>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              <FormErrorMessage>
                {" "}
                <Translate text={errors.price} />
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>
                {" "}
                <Translate text="Currency" />
              </FormLabel>
              <Select
                value={flightCurrency}
                onChange={(e) => setFlightCurrency(e.target.value)}
              >
                {availableCurrencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    <Translate text={c.label} />
                  </option>
                ))}
              </Select>
            </FormControl>

            <GridItem colSpan={{ base: 1, md: 2 }}>
              <FormControl isInvalid={errors.link}>
                <FormLabel>
                  {" "}
                  <Translate text="Booking Link" />
                </FormLabel>
                <Input value={link} onChange={(e) => setLink(e.target.value)} />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.link} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="customGreen"
            mr={3}
            onClick={handleCreate}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            <Translate text="Save" />
          </Button>
          <Button onClick={onClose} isDisabled={isSubmitting}>
            <Translate text="Cancel" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddFlight;
